import React, { useCallback, useEffect, useMemo, useState } from 'react'
import TableCell from '@material-ui/core/TableCell'

import PropTypes from 'prop-types'
import TableContainer from '@material-ui/core/TableContainer'
import Table from '@material-ui/core/Table'
import TableRow from '@material-ui/core/TableRow'
import TableBody from '@material-ui/core/TableBody'
import styled from 'styled-components'
import CircularProgress from '@material-ui/core/CircularProgress'
import Typography from '@material-ui/core/Typography'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import DialogTitle from '@material-ui/core/DialogTitle'
import moment from 'moment'
import _ from 'lodash'
import {
  ApprovalsPeopleContainer,
  ApprovalViewContainer,
  BudgetContainer,
  DetailsActionContainer,
  DetailsViewContainer,
  DetailsViewHeaderContainer,
  StyledApproveButton,
  StyledDetailsSpan,
  StyledMutedButton,
  StyledWeekContainer,
  TableWrapper,
  WeekContainer,
} from './ApprovalView.styles'
import { ProjectsTableContainer, StyledTableHead } from './ProjectsView.styles'
import EntryColumns from './Components/ApprovalsTableColumn'
import { WeekTabButton } from '../Calendar/Header/styles'
import ApprovalsActionButtons from './Components/ApprovalsActionButtons'
import Calendar from './Components/Calendar'
import { calculateBudgetAndPacingInWeek, groupTimeEntries } from './Approvals.utils'
import ApprovalProjectSelector from './Components/ApprovalProjectSelector'
import { LoadingIndicator } from '../Calendar/style'
import formatCurrency from '../../utils/formatCurrency'
import useWipData from '../WorkInProgress/useWipData'

const ApprovalsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
`
document.body.style.overflow = 'auto'

const splits = {
  EXPERT: 'A/R Passthru',
  STAFF: 'A/R Keystone',
}

const isExpert = person => _.get(person, 'employeeType', '').includes('Contractor - Expert')

const ApprovalsInformativeAdvice = () => (
  <p style={{ width: '80%', maxWidth: '1000px' }}>
    Once a timesheet is approved, the approved timesheets will flow into &nbsp;
    <a href="https://stacks.keystonestrategy.io/" target="_blank" rel="noreferrer">
      Stacks
    </a>
    &nbsp; You must manage your approvals to ensure your project is up to date. Glimpse should be a
    true reflection of actual hours worked by employees. Any write-offs must be done in Stacks.
    Please do not reject time entries in Glimpse for the purposes of write offs
  </p>
)

const ProjectApprovals = ({
  projects,
  client,
  onUpdateManyEntries,
  timeEntries: rawTimeEntries,
  timesheets,
  people,
  person,
  expenses,
  availableWeeks: allWeeks = [],
  onWritteOff,
  onReject,
  rates,
  calendarValues,
  calendarResume,
  onSplitEntry,
  onProjectSelect,
  selectedProject,
  invoices = [],
  isLoadingProjectData = false,
}) => {
  const [personDetailsId, setPersonDetailsId] = useState()
  const [showDetails, setShowDetails] = useState(false)
  const [pendingActions, setPendingActions] = useState({}) // { entryId: { action: 'approve' | 'reject', rejectionReason?: string } }
  const [showResetConfirm, setShowResetConfirm] = useState(false)
  const timeEntries = useMemo(() => {
    return groupTimeEntries(rawTimeEntries, timesheets.submitted, allWeeks)
  }, [timesheets, rawTimeEntries, allWeeks])

  const availableWeeks = useMemo(
    () =>
      allWeeks.filter(week => {
        return Object.keys(timeEntries[selectedProject?._id] || {}).includes(week.stringWeek)
      }),
    [allWeeks, timeEntries, selectedProject],
  )

  const [selectedWeek, setSelectedWeek] = useState(availableWeeks[0])
  useEffect(() => {
    setSelectedWeek(availableWeeks[0])
  }, [availableWeeks])

  const [selectedEntries, setSelectedEntries] = useState({})

  const onSelect = useCallback(
    entry => {
      if (
        selectedEntries[entry.personId] &&
        selectedEntries[entry.personId].some(currentEntry => currentEntry._id === entry._id)
      ) {
        setSelectedEntries({
          ...selectedEntries,
          [entry.personId]: selectedEntries[entry.personId].filter(
            personEntry => personEntry._id !== entry._id,
          ),
        })
        return
      }

      setSelectedEntries({
        ...selectedEntries,
        [entry.personId]: [...(selectedEntries[entry.personId] || []), entry],
      })
    },
    [selectedEntries, setSelectedEntries],
  )

  const handleApproveAll = id => {
    const filteredPersonEntries = peopleEntries[id]

    const updatedEntries = filteredPersonEntries.map(entry => {
      const pendingAction = pendingActions[entry._id]

      // If no pending action, default to approve
      if (!pendingAction || pendingAction.action === 'approve') {
        const status = entry.billable === 'ProjectRate' ? 'BILL' : entry.status

        const updatedEntry = {
          ...entry,
          status,
          shouldUpdateComment: true,
          approveState: {
            status: 'approved',
            modifiedBy: person._id,
            modifiedOn: moment.utc().toISOString(),
          },
        }

        const entryPerson = people[entry.personId]
        const isEntryFromExpert = isExpert(entryPerson)
        const split = isEntryFromExpert ? splits.EXPERT : splits.STAFF

        const currentInvoice = invoices.find(invoice => {
          const isProjectInvoice = invoice.projects.some(p => p === entry.projectId)
          const isActive = invoice.status === 'active'
          if (!isProjectInvoice || !isActive) return false

          const entryMonth = moment.utc(entry.entryDate).format('YYYY-MM')
          const invoiceMonth = moment.utc(invoice.ship_date).format('YYYY-MM')

          return entryMonth === invoiceMonth
        })

        if (currentInvoice) {
          const sections = currentInvoice.sections || []
          const section = sections.find(section => section.split === split)
          if (section) updatedEntry.quickbooksInvoiceId = section.quickbooksId
        }
        return updatedEntry
      }
      if (pendingAction.action === 'reject') {
        // Handle rejection
        return {
          ...entry,
          approveState: {
            status: 'rejected',
            rejectionReason: pendingAction.rejectionReason || '',
            modifiedOn: moment.utc().toISOString(),
          },
        }
      }

      return entry
    })

    onUpdateManyEntries(updatedEntries)

    // Clear pending actions for this person's entries
    const newPendingActions = { ...pendingActions }
    filteredPersonEntries.forEach(entry => {
      delete newPendingActions[entry._id]
    })
    setPendingActions(newPendingActions)

    // Close the person details view
    setShowDetails(false)
    setPersonDetailsId(undefined)
  }

  const availableMonths = useMemo(() => {
    return Array.from(
      new Set(availableWeeks.map(week => moment(week.start).format('YYYY-MM'))),
    ).join(',')
  }, [availableWeeks])

  const selectedMonth = useMemo(() => {
    return selectedWeek?.start.format('YYYY-MM')
  }, [selectedWeek])

  const projectsArrayWithOnlySelectedProject = useMemo(() => {
    return selectedProject ? [selectedProject] : []
  }, [selectedProject])

  const { selectedMonthProjectWipReport: wipReport, isLoading: isLoadingWipReport } = useWipData({
    client,
    projects: projectsArrayWithOnlySelectedProject,
    selectedProject,
    months: availableMonths,
    selectedMonth,
  })

  const peopleEntries =
    (timeEntries &&
      selectedProject &&
      selectedWeek &&
      timeEntries[selectedProject?._id]?.[selectedWeek?.stringWeek]) ||
    {}

  const projectExpenses = useMemo(
    () =>
      selectedProject ? expenses.filter(expense => expense.projectId === selectedProject._id) : [],
    [selectedProject, expenses],
  )

  const selectedWeekExpenses = useMemo(
    () =>
      projectExpenses.filter(expense => {
        if (!selectedWeek) return false
        return moment.utc(expense.incurredDate).isoWeek() === selectedWeek.start.isoWeek()
      }),
    [projectExpenses, selectedWeek],
  )

  const { actualPacing, actualUsed, monthBudget, noBudget } = useMemo(() => {
    if (!timeEntries || !selectedProject || !selectedWeek || !wipReport)
      return {
        noBudget: true,
      }
    return calculateBudgetAndPacingInWeek(
      selectedWeek.start,
      selectedProject,
      wipReport,
      availableWeeks,
    )
  }, [selectedProject, selectedWeek, wipReport, availableWeeks])

  const handleShowDetails = id => {
    setPersonDetailsId(id)
    setShowDetails(true)
  }

  const handleReset = id => {
    const filteredPersonEntries = peopleEntries[id]

    // Reset comments to originalComment
    const updatedEntries = filteredPersonEntries.map(entry => ({
      ...entry,
      comment: entry.originalComment || entry.comment,
    }))

    onUpdateManyEntries(updatedEntries)

    // Clear pending actions for this person's entries (defaults back to approve)
    const newPendingActions = { ...pendingActions }
    filteredPersonEntries.forEach(entry => {
      delete newPendingActions[entry._id]
    })
    setPendingActions(newPendingActions)
  }
  if (!selectedProject)
    return (
      <ApprovalsContainer>
        <ApprovalViewContainer>
          <div>
            <ApprovalProjectSelector
              projects={projects}
              selectedProject={selectedProject}
              onSelectProject={onProjectSelect}
            />
          </div>
        </ApprovalViewContainer>
      </ApprovalsContainer>
    )

  if (isLoadingProjectData)
    return (
      <ApprovalsContainer>
        <ApprovalViewContainer>
          <div>
            <ApprovalProjectSelector
              projects={projects}
              selectedProject={selectedProject}
              onSelectProject={onProjectSelect}
            />
          </div>
          <LoadingIndicator>
            <CircularProgress />
          </LoadingIndicator>
        </ApprovalViewContainer>
      </ApprovalsContainer>
    )

  const renderPersonDetails = (
    <DetailsViewContainer data-testid="approvals.personDetails">
      <DetailsViewHeaderContainer>
        <StyledDetailsSpan>{people[personDetailsId]?.fullName}</StyledDetailsSpan>
        <div style={{ display: 'flex', gap: '8px' }}>
          <StyledMutedButton
            data-testid="approvals.personDetails.reset"
            onClick={() => setShowResetConfirm(true)}
          >
            Reset
          </StyledMutedButton>
          <StyledApproveButton
            data-testid="approvals.personDetails.close"
            onClick={() => setShowDetails(false)}
          >
            Close
          </StyledApproveButton>
        </div>
      </DetailsViewHeaderContainer>
      <Dialog
        open={showResetConfirm}
        onClose={() => setShowResetConfirm(false)}
        data-testid="approvals.resetConfirmDialog"
      >
        <DialogTitle>Reset Changes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            All comments and actions will be reverted to their original state. This cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <StyledMutedButton
            data-testid="approvals.resetConfirm.cancel"
            onClick={() => setShowResetConfirm(false)}
          >
            Cancel
          </StyledMutedButton>
          <StyledApproveButton
            data-testid="approvals.resetConfirm.confirm"
            onClick={() => {
              handleReset(personDetailsId)
              setShowResetConfirm(false)
            }}
          >
            Confirm
          </StyledApproveButton>
        </DialogActions>
      </Dialog>
      <TableContainer component={ProjectsTableContainer} rows={6}>
        <Table aria-label="simple table" style={{ height: '100%' }}>
          <StyledTableHead>
            <TableRow>
              <TableCell width={50}>Date</TableCell>
              <TableCell width={200}>Comment</TableCell>

              <TableCell width={50}>Hours</TableCell>
              <TableCell width={50}>Billable</TableCell>
              <TableCell width={50}>Rate</TableCell>
              <TableCell
                style={{
                  minWidth: '150px',
                }}
                width={80}
              >
                Total
              </TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {peopleEntries &&
              personDetailsId &&
              peopleEntries[personDetailsId] &&
              peopleEntries[personDetailsId].map(entry => {
                return (
                  <TableRow key={entry._id}>
                    <EntryColumns
                      onUpdateEntry={onUpdateManyEntries}
                      entry={entry}
                      onSelect={onSelect}
                      person={person}
                      client={client}
                      rates={rates[selectedProject?._id]}
                      project={selectedProject}
                      pendingActions={pendingActions}
                      setPendingActions={setPendingActions}
                    />
                  </TableRow>
                )
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </DetailsViewContainer>
  )

  return (
    <ApprovalsContainer>
      <ApprovalViewContainer>
        <div>
          <ApprovalProjectSelector
            projects={projects}
            selectedProject={selectedProject}
            onSelectProject={onProjectSelect}
          />
          <div style={{ marginBottom: 30 }} />
          {calendarValues.current?.[selectedProject?._id] && (
            <Calendar
              data={calendarValues.current[selectedProject._id]}
              resume={calendarResume.current[selectedProject._id]}
              project={selectedProject}
            />
          )}
        </div>
        <ApprovalsPeopleContainer>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 20,
              width: '100%',
              marginBottom: 20,
            }}
          >
            <>
              <BudgetContainer data-testid="approvals.budget.project">
                <Typography>
                  <strong>
                    Project Budget{' '}
                    {selectedWeek ? `(${selectedWeek.start.format('MMMM YYYY')})` : ''}
                  </strong>
                </Typography>
                <Typography>
                  {!noBudget && formatCurrency(monthBudget, selectedProject.CurrencyIsoCode)}
                  {noBudget && 'No budget available'}
                </Typography>
              </BudgetContainer>
              <BudgetContainer data-testid="approvals.budget.actual">
                <Typography>
                  <strong>
                    Project Actual{' '}
                    {selectedWeek ? `(${selectedWeek.start.format('MMMM YYYY')})` : ''}
                  </strong>
                </Typography>
                <Typography>
                  {!noBudget && formatCurrency(actualUsed, selectedProject.CurrencyIsoCode)}
                  {noBudget && 'No actuals available'}
                </Typography>
              </BudgetContainer>
              <BudgetContainer data-testid="approvals.budget.pacing">
                <Typography>
                  <strong>
                    Pacing {selectedWeek ? `(${selectedWeek.start.format('MMMM YYYY')})` : ''}
                  </strong>
                </Typography>
                <Typography>
                  {noBudget
                    ? 'No pacing available'
                    : formatCurrency(Math.abs(actualPacing), selectedProject.CurrencyIsoCode)}
                </Typography>
              </BudgetContainer>
            </>
          </div>

          <StyledWeekContainer data-testid="approvals.weekTabs">
            {availableWeeks.length > 0 &&
              availableWeeks.map(week => {
                return (
                  <WeekTabButton
                    key={week.stringWeek}
                    data-testid={`approvals.weekTab.${week.stringWeek.replace(/\s+/g, '-')}`}
                    minWidth="200px"
                    onClick={() => {
                      setSelectedWeek(week)
                    }}
                    selected={week.stringWeek === selectedWeek?.stringWeek}
                  >
                    {week.stringWeek}
                  </WeekTabButton>
                )
              })}
          </StyledWeekContainer>
          <Typography
            style={{ textAlign: 'center', marginTop: '10px', marginBottom: '10px', color: '#666' }}
          >
            This view is displaying total hours (Billable and non-billable)*
          </Typography>

          <WeekContainer>
            <TableWrapper>
              <TableContainer component={ProjectsTableContainer} rows={5}>
                <Table aria-label="simple table">
                  <StyledTableHead>
                    <TableRow>
                      <TableCell align="Left">Person</TableCell>
                      <TableCell align="center">Sun</TableCell>
                      <TableCell align="center">Mon</TableCell>
                      <TableCell align="center">Tues</TableCell>
                      <TableCell align="center">Wed</TableCell>
                      <TableCell align="center">Thur</TableCell>
                      <TableCell align="center">Fri</TableCell>
                      <TableCell align="center">Sat</TableCell>
                      <TableCell align="center">
                        <strong>Totals</strong>
                      </TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </StyledTableHead>

                  {peopleEntries &&
                    Object.entries(peopleEntries).map(([id, entries]) => {
                      const hoursPerDay = entries.reduce(
                        (acc, entry) => {
                          acc[entry.weekDay] = (acc[entry.weekDay] || 0) + +entry.durationInHours
                          return acc
                        },
                        {
                          Sunday: 0,
                          Monday: 0,
                          Tuesday: 0,
                          Wednesday: 0,
                          Thursday: 0,
                          Friday: 0,
                          Saturday: 0,
                        },
                      )

                      return (
                        <TableBody key={id}>
                          <TableRow data-testid={`approvals.personRow.${id}`}>
                            <TableCell align="left">{people[id].fullName}</TableCell>
                            <TableCell align="center">{hoursPerDay.Sunday.toFixed(2)}</TableCell>
                            <TableCell align="center">{hoursPerDay.Monday.toFixed(2)}</TableCell>
                            <TableCell align="center">{hoursPerDay.Tuesday.toFixed(2)}</TableCell>
                            <TableCell align="center">{hoursPerDay.Wednesday.toFixed(2)}</TableCell>
                            <TableCell align="center">{hoursPerDay.Thursday.toFixed(2)}</TableCell>
                            <TableCell align="center">{hoursPerDay.Friday.toFixed(2)}</TableCell>
                            <TableCell align="center">{hoursPerDay.Saturday.toFixed(2)}</TableCell>
                            <TableCell align="center">
                              <strong>
                                {Object.values(hoursPerDay)
                                  .reduce((acc, hours) => acc + hours, 0)
                                  .toFixed(2)}
                              </strong>
                            </TableCell>
                            <TableCell align="center">
                              <DetailsActionContainer>
                                <StyledMutedButton
                                  data-testid={`approvals.personDetailsBtn.${id}`}
                                  onClick={() => handleShowDetails(people[id]?._id)}
                                >
                                  Details
                                </StyledMutedButton>
                                <StyledApproveButton
                                  data-testid={`approvals.personSubmitBtn.${id}`}
                                  onClick={() => handleApproveAll(people[id]?._id)}
                                >
                                  Submit
                                </StyledApproveButton>
                              </DetailsActionContainer>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      )
                    })}
                </Table>
              </TableContainer>
            </TableWrapper>
            {showDetails && renderPersonDetails}
          </WeekContainer>
          <ApprovalsInformativeAdvice />
        </ApprovalsPeopleContainer>
      </ApprovalViewContainer>
    </ApprovalsContainer>
  )
}

ProjectApprovals.propTypes = {
  projects: PropTypes.arrayOf(PropTypes.object),
  client: PropTypes.object.isRequired,
  onUpdateManyEntries: PropTypes.func.isRequired,
  timeEntries: PropTypes.object,
  availableWeeks: PropTypes.object,
  people: PropTypes.object,
  timesheets: PropTypes.object,
  person: PropTypes.object,
  onWritteOff: PropTypes.func,
  onReject: PropTypes.func,
  rates: PropTypes.object,
  calendarValues: PropTypes.object,
  calendarResume: PropTypes.object,
  onSplitEntry: PropTypes.func,
  onProjectSelect: PropTypes.func,
  selectedProject: PropTypes.object,
  expenses: PropTypes.arrayOf(PropTypes.object),
  isLoadingProjectData: PropTypes.bool,
}

export default ProjectApprovals
